import React, { memo, useState } from 'react';
import { User } from 'lucide-react';
import { FaXTwitter } from 'react-icons/fa6';
import { SiSubstack, SiSpotify, SiPocket, SiGoodreads, SiYoutube, SiReddit } from 'react-icons/si';
import { FaAmazon, FaApple } from 'react-icons/fa';

const connectedAccountsData = [
  { id: 'twitter', name: 'X (Twitter)', Icon: FaXTwitter, color: 'bg-black', description: 'Import posts and bookmarks' },
  { id: 'substack', name: 'Substack', Icon: SiSubstack, color: 'bg-orange-500', description: 'Import subscriptions and notes' },
  { id: 'apple-books', name: 'Apple Books', Icon: FaApple, color: 'bg-gray-800', description: 'Sync highlights and notes' },
  { id: 'spotify', name: 'Spotify', Icon: SiSpotify, color: 'bg-green-500', description: 'Import podcast history' },
  { id: 'pocket', name: 'Pocket', Icon: SiPocket, color: 'bg-pink-500', description: 'Import saved articles' },
  { id: 'goodreads', name: 'Goodreads', Icon: SiGoodreads, color: 'bg-amber-700', description: 'Sync reading list' },
  { id: 'youtube', name: 'YouTube', Icon: SiYoutube, color: 'bg-red-500', description: 'Import watch history' },
  { id: 'kindle', name: 'Kindle', Icon: FaAmazon, color: 'bg-blue-600', description: 'Sync highlights and notes' },
  { id: 'reddit', name: 'Reddit', Icon: SiReddit, color: 'bg-orange-600', description: 'Import saved posts' },
];

const ProfileView = memo(() => {
  const [connectedAccounts, setConnectedAccounts] = useState(new Set());

  const toggleAccount = (accountId) => {
    setConnectedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) {
        next.delete(accountId);
      } else {
        next.add(accountId);
      }
      return next;
    });
  };

  return (
    <div className="pt-4 pb-20 px-5 font-geist editorial-paper min-h-full">
      <header className="mb-6 border-y-2 border-black/45 py-2">
        <p className="text-[10px] uppercase tracking-[0.22em] text-black/60 font-semibold">Profile</p>
        <div className="mt-2 flex items-center gap-3">
          <div className="w-10 h-10 border-2 border-black/45 bg-white flex items-center justify-center">
            <User className="w-5 h-5 text-black" />
          </div>
          <div>
            <h2 className="font-instrument text-[32px] leading-[0.92] text-black">Eddy Escarole</h2>
            <p className="text-[10px] uppercase tracking-[0.16em] text-black/60 font-semibold">Reader Edition</p>
          </div>
        </div>
      </header>

      <section className="border-2 border-black/45 bg-[#F8F5EE] p-4">
        <h3 className="font-instrument text-[28px] leading-[0.9] text-black">Connected Accounts</h3>
        <p className="text-[10px] uppercase tracking-[0.16em] text-black/60 font-semibold mt-1 mb-4">
          Link accounts to sync saved content
        </p>

        <div className="space-y-2">
          {connectedAccountsData.map((account) => {
            const isConnected = connectedAccounts.has(account.id);
            const IconComponent = account.Icon;
            return (
              <div key={account.id} className="flex items-center justify-between p-3 border-2 border-black/30 bg-white">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-8 h-8 ${account.color} flex items-center justify-center text-white`}>
                    <IconComponent className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-black truncate">{account.name}</p>
                    <p className="text-[11px] text-black/60 truncate">{account.description}</p>
                  </div>
                </div>
                <button
                  onClick={() => toggleAccount(account.id)}
                  role="switch"
                  aria-checked={isConnected}
                  aria-label={`${isConnected ? 'Disconnect' : 'Connect'} ${account.name}`}
                  className={`w-11 h-6 border-2 p-0.5 flex items-center transition-colors ${
                    isConnected
                      ? 'bg-emerald-800 border-emerald-800 justify-end'
                      : 'bg-[#F8F5EE] border-emerald-800/35 hover:border-emerald-800/55'
                  }`}
                >
                  <span className="h-3.5 w-3.5 bg-white" aria-hidden />
                </button>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
});

ProfileView.displayName = 'ProfileView';

export default ProfileView;
